import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { useDeleteNote, useDeleteTopic } from '@/hooks/api/dashboard'
import { fireAlertToast, getAlertCopy } from '@/design-system/alerts'
import type { AlbumPhoto } from '../types'

/**
 * Owner delete actions — photo (note) and whole album.
 * Meshdom deletes the note for a photo batch; multi-ego notes remove all siblings.
 */
export function useAlbumDelete(
  albumId: string | undefined,
  photos: AlbumPhoto[],
  setPhotos: (updater: (prev: AlbumPhoto[]) => AlbumPhoto[]) => void,
  opts?: { isOwner?: boolean; onAlbumDeleted?: () => void },
) {
  const deleteNote = useDeleteNote(false)
  const deleteTopic = useDeleteTopic(albumId || '')
  const [deletingPhoto, setDeletingPhoto] = useState(false)
  const [deletingAlbum, setDeletingAlbum] = useState(false)

  const siblingsFor = useCallback(
    (photo: AlbumPhoto) => {
      if (!photo.noteId) return [photo]
      return photos.filter((p) => p.noteId === photo.noteId)
    },
    [photos],
  )

  const deletePhoto = useCallback(
    async (photo: AlbumPhoto) => {
      if (!albumId || !photo.noteId) {
        fireAlertToast('photo.deleteUnavailable')
        return false
      }
      if (opts?.isOwner === false) {
        fireAlertToast('photo.deleteForbidden')
        return false
      }

      const batch = siblingsFor(photo)
      setDeletingPhoto(true)
      // Optimistic remove
      setPhotos((prev) => prev.filter((p) => p.noteId !== photo.noteId))

      try {
        await deleteNote.mutateAsync({ topicID: albumId, noteID: photo.noteId })
        if (batch.length > 1) {
          fireAlertToast('photo.deletedPlural', { title: `Removed ${batch.length} photos` })
        } else {
          fireAlertToast('photo.deleted')
        }
        return true
      } catch (e) {
        // Revert
        setPhotos((prev) => {
          const missing = batch.filter((b) => !prev.some((p) => p.id === b.id))
          return missing.length ? [...prev, ...missing] : prev
        })
        toast.error(e instanceof Error ? e.message : getAlertCopy('photo.deleteFailed').title)
        return false
      } finally {
        setDeletingPhoto(false)
      }
    },
    [albumId, deleteNote, opts?.isOwner, setPhotos, siblingsFor],
  )

  const deleteAlbum = useCallback(async () => {
    if (!albumId) return false
    if (opts?.isOwner === false) {
      fireAlertToast('album.deleteForbidden')
      return false
    }
    setDeletingAlbum(true)
    try {
      await deleteTopic.mutateAsync()
      fireAlertToast('album.deleted')
      opts?.onAlbumDeleted?.()
      return true
    } catch (e) {
      toast.error(e instanceof Error ? e.message : getAlertCopy('album.deleteFailed').title)
      return false
    } finally {
      setDeletingAlbum(false)
    }
  }, [albumId, deleteTopic, opts])

  return {
    deletePhoto,
    deleteAlbum,
    siblingsFor,
    deletingPhoto,
    deletingAlbum,
  }
}
